import { useDynamicContext } from '@page-components/shipment/DynamicFields';
import { ReadOnlyFields } from './ReadOnlyField';
import { DynamicFieldType } from '@page-components/shipment/common';
import { CommonUtility } from '@utility/common';
import { PermissionFormField } from '../PermissionFormField';
import { FIELD_INPUT, FIELD_INPUT_INVALID } from '@elements/form-field/styles';
import { Checkbox, cn } from '@skupreme/shared/ui';
import { CalendarInput } from './CalendarInput';
import { InputContainer } from '../styles/common';
import { CurrencyMaskInput, NumberMaskInput } from '@elements/CustomMaskInput';
import { CenterDropdownStyle } from '@elements/Dropdown';
import { dynamicSearchFunction, parseDynamicOptions } from '../Utils/common';
import DatePicker from 'react-datepicker';
import { DateFormat, DateUtility } from '@utility/date';
import { NestedField } from './NestedField';
import { HoverBorderButton } from '@elements/Button';
import { InfoIcon, PlusIcon } from '@phosphor-icons/react';
import { FormLabel } from '@page-components/components/common';
import { SKUQuickView } from '../Label/SkuQuickView';
import PermissionTooltip from '@components/PermissionTooltip';
import { Link } from 'react-router';
import { FlexNestedField } from './FlexNestedField';
import { CheckboxButton } from '../CheckboxButton';
import { DynamicFieldUploadV2 } from '../FileUpload/FieldUploadV2';
import { DarkPopup } from '@elements/Popup';
import { DangerText, MuteText } from '@elements/Common';
import { TrackingRenderer } from '../Tracking';
import { RadioArray } from '../RadioArray';
import { RadioCard } from '../RadioCard';
import { RadioDropdown } from '../RadioDropdown';
import { ShippingBoxPopup } from '../ShippingBox/popup';
import { PalletBoxPopup } from '../PalletBox/popup';
import type {
  DynamicListArray,
  ShipmentDynamicFieldType,
} from '@hooks/shipment';

type FieldProps = {
  value: any;
  error: any;
  onChange?: (name: string, value: any, callAttributeApi?: boolean) => void;
  hideLabel?: boolean;
  index?: number;
  addNestedField?: (item?: ShipmentDynamicFieldType) => void;
  removeNestedField?: (item?: string, idx?: number) => void;
  onPressEnter?: () => void;
  style?: boolean;
  item: ShipmentDynamicFieldType;
  disabled?: boolean;
  hidden?: boolean;
  layoutClass?: string;
  rowValue?: any; // for flex array
};

export function Field({
  value,
  error,
  item,
  onChange,
  hideLabel,
  index,
  addNestedField,
  removeNestedField,
  onPressEnter,
  style = true,
  layoutClass = '',
  disabled = false,
  hidden = false,
  rowValue = null, // for flex array
}: FieldProps) {
  const {
    // @ts-expect-error - TS2339: Property 'is2DBarcode' does not exist on type '{}'.
    is2DBarcode,
    // @ts-expect-error - TS2339: Property 'actionAllowed' does not exist on type '{}'.
    actionAllowed,
    // @ts-expect-error - TS2339: Property 'readOnly' does not exist on type '{}'.
    readOnly,
    // @ts-expect-error - TS2339: Property 'actionButtonClick' does not exist on type '{}'.
    actionButtonClick,
    // @ts-expect-error - TS2339: Property 'countryOptions' does not exist on type '{}'.
    countryOptions,
    // @ts-expect-error - TS2339: Property 'removeDocument' does not exist on type '{}'.
    removeDocument,
    // @ts-expect-error - TS2339: Property 'uploadDocument' does not exist on type '{}'.
    uploadDocument,
    // @ts-expect-error - TS2339: Property 'uploadDocument' does not exist on type '{}'.
    isFBA,
  } = useDynamicContext() || {};
  const allowedAction = actionAllowed || !!item.isAlwaysAllowed;

  if (item.isHidden || (item.hideField && !readOnly)) {
    return <></>;
  }

  if (
    (item.readOnly || readOnly) &&
    ![
      DynamicFieldType.array,
      DynamicFieldType.file,
      DynamicFieldType.button,
      DynamicFieldType.flexArray,
    ].includes(item.inputType)
  ) {
    return (
      <ReadOnlyFields
        item={item}
        layoutClass={layoutClass}
        hideLabel={hideLabel}
        value={value}
      />
    );
  }

  const onFieldChange = value => {
    onChange?.(item.name, value, item.callAttributeApi);
  };

  const commonArrayProps = {
    addNestedField,
    errors: error,
    item,
    onChange,
    removeNestedField,
    values: value || [],
  };

  const onKeyDown = e => {
    if (e.key === 'Enter' && e.preventDefault() && onPressEnter) {
      onPressEnter?.();
    }
  };

  /* @ts-expect-error - TS2339: Property 'documents' does not exist on type '{}'. */
  const { documents } = useDynamicContext() || {};

  let files = documents;

  let listOfItems: DynamicListArray | null = null;
  // @ts-expect-error Argument of type 'string | undefined' is not assignable to parameter of type 'unknown[]
  if (CommonUtility.isValidArray(item.list)) {
    listOfItems = item.list;
  } else if (CommonUtility.isValidObject(item.list) && rowValue) {
    listOfItems = item.list[rowValue.shipmentId] || null;
  }

  switch (item.inputType) {
    case DynamicFieldType.text:
      return (
        <PermissionFormField
          layoutClass={layoutClass}
          hideLabel={hideLabel}
          item={item}
          hidden={hidden}
          disabled={disabled}
          error={error}
        >
          <input
            className={cn(FIELD_INPUT, !!error && FIELD_INPUT_INVALID)}
            disabled={!allowedAction || disabled}
            name={item.name}
            onChange={e => onFieldChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={item.placeholder || item.label}
            required={item.required && !hideLabel}
            value={value ?? ''}
          />
        </PermissionFormField>
      );
    case DynamicFieldType.number:
      return (
        <>
          <PermissionFormField
            layoutClass={layoutClass}
            hideLabel={hideLabel}
            item={item}
            hidden={hidden}
            disabled={disabled}
            error={error}
          >
            <InputContainer>
              {item.preFix && <div className="sk:mr-1!">{item.preFix}</div>}
              <NumberMaskInput
                onKeyDown={onKeyDown}
                disabled={!allowedAction || disabled}
                name={item.name}
                placeholder={item.placeholder || item.label}
                value={value}
                maxValue={item.maskOptions?.maxValue}
                maskOptions={{
                  allowDecimal: true,
                  // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'decimalLimit' does not ex...
                  decimalLimit: 1,
                  includeThousandsSeparator: true,
                  thousandsSeparatorSymbol: ',',
                }}
                onChange={value =>
                  onFieldChange(
                    CommonUtility.isValidNumber(value) ? Number(value) : value,
                  )
                }
                style={{
                  width: item.preFix ? 'auto' : '100%',
                }}
              />
            </InputContainer>
          </PermissionFormField>
          {item.labelRight ? (
            <div className="sk:my-3.5!">{item.labelRight}</div>
          ) : (
            <></>
          )}
        </>
      );
    case DynamicFieldType.currency:
      return (
        <PermissionFormField
          layoutClass={layoutClass}
          hideLabel={hideLabel}
          item={item}
          hidden={hidden}
          disabled={disabled}
          error={error}
        >
          <CurrencyMaskInput
            disabled={!allowedAction || disabled}
            maskOptions={{ decimalLimit: 2 }}
            onChange={value => onFieldChange(value)}
            onKeyDown={onKeyDown}
            placeholder={item.label}
            value={value}
          />
        </PermissionFormField>
      );
    case DynamicFieldType.dropdown:
      return (
        <PermissionFormField
          layoutClass={layoutClass}
          hideLabel={hideLabel}
          item={item}
          hidden={hidden}
          disabled={disabled}
          error={error}
        >
          <CenterDropdownStyle
            placeholder={item.label}
            disabled={!allowedAction || disabled}
            selectOnBlur={item.countryOptions ? true : false}
            selection
            search={dynamicSearchFunction}
            className={item.countryOptions ? 'country-dropdown' : ''}
            options={
              item.countryOptions
                ? countryOptions
                : parseDynamicOptions(item.options as any)
            }
            onChange={(_, { value }) => {
              onFieldChange(value);
              if (item.triggerApi && actionButtonClick) {
                actionButtonClick({
                  ...item,
                  triggerDynamicApi: true,
                  extraParams: {
                    [item.name]: value,
                  },
                });
              }
              if (item.onChangeActionApi && actionButtonClick) {
                actionButtonClick({
                  ...item,
                  actionTriggerApi: true,
                  name: item.onChangeActionApi,
                  extraParams: {
                    [item.name]: value,
                  },
                });
              }
            }}
            value={value}
          />
        </PermissionFormField>
      );
    case DynamicFieldType.date:
      return (
        <PermissionFormField
          layoutClass={layoutClass}
          hideLabel={hideLabel}
          item={item}
          hidden={hidden}
          disabled={disabled}
          error={error}
          fieldClass={hideLabel ? 'table-input' : ''}
        >
          <DatePicker
            customInput={<CalendarInput />}
            dateFormat={DateFormat.datepicker}
            disabled={!allowedAction}
            minDate={
              item.allowPastDate
                ? null
                : item.allowCurrentDate === false
                  ? DateUtility.addDays(new Date(), 1)
                  : new Date()
            }
            onChange={date => onFieldChange(date)}
            placeholderText={item.label}
            selected={value ? new Date(value) : null}
          />
        </PermissionFormField>
      );
    case DynamicFieldType.radio:
      return (
        <PermissionFormField
          layoutClass={layoutClass}
          hideLabel={hideLabel}
          item={item}
          hidden={hidden}
          disabled={disabled}
          error={error}
        >
          {(item.options || []).map(option => (
            <label
              className="sk:mr-3.5! sk:inline-flex sk:cursor-pointer sk:items-center sk:gap-1.5"
              key={option.value}
            >
              <input
                type="radio"
                className="sk:m-0 sk:accent-primary"
                checked={value === option.value || (!option.value && !value)}
                disabled={!allowedAction || disabled}
                name={`${index}-${item.name}`}
                onChange={() => onFieldChange(option.value)}
                value={option.value}
              />
              {option.text}
            </label>
          ))}
        </PermissionFormField>
      );
    case DynamicFieldType.array:
      return (
        <>
          <div
            className="sk:w-full sk:flex-none sk:px-4 sk:p-0!"
            key={item.name}
          >
            <div>
              <NestedField {...commonArrayProps} />
            </div>
          </div>
          {item.multiple && !readOnly && item.allowAdd && (
            <div
              className={`sk:my-2! ${
                item.addButtonLayoutClass
                  ? item.addButtonLayoutClass
                  : 'sk:w-3/12 sk:flex-none sk:px-4'
              }`}
            >
              <HoverBorderButton
                className="flex-btn"
                disabled={!allowedAction}
                onClick={() => addNestedField?.(item)}
                type="button"
              >
                <PlusIcon className="sk:mr-1!" /> {item.addButtonText || ''}
              </HoverBorderButton>
            </div>
          )}
        </>
      );
    case DynamicFieldType.flexArray:
      return (
        <div className="sk:w-full sk:flex-none sk:px-4 sk:p-0!" key={item.name}>
          <div>
            <FlexNestedField {...commonArrayProps} />
          </div>
        </div>
      );
    case DynamicFieldType.label:
      return (
        <div
          className={
            layoutClass
              ? `${layoutClass} sk:my-2!`
              : `sk:w-full sk:flex-none sk:px-4 sk:my-2!`
          }
          key={item.name}
        >
          {!item.header && (
            <FormLabel label={item.label} info_tooltip={item.infoTooltip} />
          )}
          <span>{value}</span>
          {listOfItems && <SKUQuickView items={listOfItems} />}
        </div>
      );
    case DynamicFieldType.currencyLabel:
      return (
        <div
          className={
            layoutClass
              ? `${layoutClass} sk:my-2!`
              : `${hideLabel ? 'sk:w-full sk:flex-none sk:px-4' : 'sk:w-3/12 sk:flex-none sk:px-4'} sk:my-2!`
          }
          key={item.name}
        >
          {CommonUtility.currencyFormat(value, item.currency)}
        </div>
      );
    case DynamicFieldType.button:
      return (
        <div
          className={
            layoutClass
              ? `sk:my-2! ${layoutClass}`
              : `${style ? 'sk:w-full sk:flex-none sk:px-4 sk:text-right' : 'sk:min-w-0 sk:flex-1 sk:px-4'} sk:my-2!`
          }
          key={item.name}
        >
          <PermissionTooltip
            disabled={disabled}
            hidden={hidden}
            position="bottom center"
            wrapperClassName={
              disabled
                ? 'sk:cursor-not-allowed sk:opacity-70 sk:[&_*]:cursor-not-allowed'
                : ''
            }
          >
            {item.href ? (
              <Link to={item.href}>
                <a target="_blank">
                  <HoverBorderButton
                    className={disabled ? 'primary-btn' : ''}
                    disabled={!allowedAction || disabled}
                    fluid={!!item.fluid}
                    type="button"
                  >
                    {item.label}
                  </HoverBorderButton>
                </a>
              </Link>
            ) : (
              <HoverBorderButton
                className={disabled ? 'primary-btn' : ''}
                disabled={!allowedAction || disabled}
                fluid={!!item.fluid}
                onClick={() => {
                  actionButtonClick?.(item);
                }}
                type="button"
              >
                {item.label}
              </HoverBorderButton>
            )}
          </PermissionTooltip>
        </div>
      );
    case DynamicFieldType.checkbox:
      return (
        <div
          className={
            layoutClass
              ? `${layoutClass} sk:my-2!`
              : 'sk:w-full sk:flex-none sk:px-4 sk:my-2!'
          }
          key={item.name}
        >
          <div className="field">
            <PermissionTooltip
              disabled={disabled}
              hidden={hidden}
              position="bottom left"
            >
              <Checkbox
                isSelected={value || false}
                className="sk:mr-2!"
                isDisabled={!allowedAction || disabled}
                name={`${index}-${item.name}`}
                onChange={checked => onFieldChange(checked)}
              >
                {hideLabel ? '' : item.label}
              </Checkbox>
            </PermissionTooltip>
          </div>
        </div>
      );
    case DynamicFieldType.checkboxButton:
      return (
        <CheckboxButton
          key={item.name}
          item={item}
          actionButtonClick={actionButtonClick}
        />
      );
    case DynamicFieldType.file:
      if (value) {
        files = files?.filter(x => x._id === value);
      }

      return (
        <DynamicFieldUploadV2
          actionAllowed={actionAllowed}
          className={layoutClass}
          documents={files}
          fields={[item]}
          removeDocument={removeDocument}
          uploadDocument={uploadDocument}
        />
      );
    case DynamicFieldType.icon:
      return (
        <div className={`${layoutClass} sk:p-1! sk:text-right sk:my-2!`}>
          <DarkPopup
            trigger={<InfoIcon size={20} className="sk:text-fg-tertiary" />}
            position="bottom center"
          >
            <MuteText>{item.label}</MuteText>
          </DarkPopup>
        </div>
      );
    case DynamicFieldType.timeline:
    case DynamicFieldType.tracking:
      return (
        <div className="sk:my-2! sk:w-full sk:flex-none sk:px-4 sk:text-center">
          <TrackingRenderer fields={item.nestedFields || []} />
        </div>
      );
    case DynamicFieldType.radioArray:
      return (
        /* @ts-expect-error - TS2739: Type '{ fields: any; item: any; label: any; name: any; onCha... */
        <RadioArray
          fields={item.nestedFields}
          item={item}
          label={item.label}
          name={item.name}
          onChange={onChange}
          values={value}
          actionButtonClick={actionButtonClick}
        />
      );
    case DynamicFieldType.radioCard:
      return (
        <RadioCard
          actionAllowed={actionAllowed}
          errors={error}
          item={item}
          // @ts-expect-error - TS2322: Type '(name: string, value: any, callAttributeApi?: boolean | undefined
          onChange={onChange}
          readOnly={readOnly}
          value={value || []}
        />
      );
    case DynamicFieldType.radioDropdown:
      return (
        <RadioDropdown
          actionAllowed={actionAllowed}
          errors={error}
          item={item}
          // @ts-expect-error - TS2322: Type '(name: string, value: any, callAttributeApi?: boolean | undefined
          onChange={onChange}
          readOnly={readOnly}
          value={value || []}
        />
      );
    case DynamicFieldType.popupBoxes:
      return (
        <div
          className={
            layoutClass
              ? `${layoutClass} sk:my-2!`
              : 'sk:w-full sk:flex-none sk:px-4 sk:my-2!'
          }
          key={item.name}
        >
          {!item.header && (
            <FormLabel
              requried={item.required}
              label={item.label}
              info_tooltip={item.infoTooltip}
            />
          )}
          <ShippingBoxPopup
            showItems={!is2DBarcode}
            value={value}
            onChange={onChange as any}
            item={item}
            rowValue={rowValue}
            isFBA={isFBA}
          />
          {error && <DangerText>{error}</DangerText>}
        </div>
      );
    case DynamicFieldType.popupPallet:
      return (
        <div
          className={
            layoutClass
              ? `${layoutClass} sk:my-2!`
              : 'sk:w-full sk:flex-none sk:px-4 sk:my-2!'
          }
          key={item.name}
        >
          {!item.header && (
            <FormLabel
              requried={item.required}
              label={item.label}
              info_tooltip={item.infoTooltip}
            />
          )}
          <PalletBoxPopup
            value={value}
            onChange={onChange as any}
            item={item}
          />
          {error && <DangerText>{error}</DangerText>}
        </div>
      );
    default:
      break;
  }
}
